import { USAGE_KINDS, type UsageByKind, type UsageProviderTokens } from '@rp/contracts'
import { isApiError } from '../../api/client'
import { Card, EmptyState, Spinner } from '../../design'
import { LOCALE } from '../../lib/time'
import { canManageSettings } from '../../session/roles'
import { useReadySession } from '../../session/useSession'
import { useTenantSettings } from '../settings/queries'
import { BudgetPanel } from './BudgetPanel'
import { CostEstimate } from './CostEstimate'
import { USAGE_COPY } from './copy'
import { useUsageSummary, useUsageTrend } from './queries'

const numberFormat = new Intl.NumberFormat(LOCALE)
const PERCENT = 100
const NO_PEAK = 0

const shareOf = (value: number, peak: number): number => (peak === NO_PEAK ? NO_PEAK : Math.round((value / peak) * PERCENT))

const TallyList = ({ rows }: { rows: readonly { key: string; label: string; value: number }[] }) => (
  <ul className="usage-list">
    {rows.map((row) => (
      <li key={row.key} className="usage-row">
        <span className="usage-row-label">{row.label}</span>
        <span className="usage-row-value num">{numberFormat.format(row.value)}</span>
      </li>
    ))}
  </ul>
)

const VolumeCard = ({ byKind }: { byKind: UsageByKind }) => (
  <Card as="section" className="usage-card">
    <h2>{USAGE_COPY.volumeHeading}</h2>
    <TallyList rows={USAGE_KINDS.map((kind) => ({ key: kind, label: USAGE_COPY.kinds[kind], value: byKind[kind] }))} />
  </Card>
)

const ProviderCard = ({ byProvider }: { byProvider: readonly UsageProviderTokens[] }) => (
  <Card as="section" className="usage-card">
    <h2>{USAGE_COPY.providerHeading}</h2>
    <TallyList
      rows={byProvider.map((row) => ({
        key: row.provider ?? USAGE_COPY.providerless,
        label: row.provider ?? USAGE_COPY.providerless,
        value: row.tokens,
      }))}
    />
  </Card>
)

const TrendCard = () => {
  const trend = useUsageTrend()
  if (trend.data === undefined) return null
  const peak = Math.max(...trend.data.daily.map((day) => day.byKind.llm_tokens), NO_PEAK)
  return (
    <Card as="section" className="usage-card">
      <h2>{USAGE_COPY.trendHeading}</h2>
      {peak === NO_PEAK ? (
        <p className="usage-updated">{USAGE_COPY.trendEmpty}</p>
      ) : (
        <ul className="usage-trend">
          {trend.data.daily.map((day) => (
            <li key={day.day} title={USAGE_COPY.trendDayLabel(day.day, numberFormat.format(day.byKind.llm_tokens))}>
              <span style={{ blockSize: `${shareOf(day.byKind.llm_tokens, peak)}%` }} />
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

const UsageReport = () => {
  const usage = useUsageSummary()
  const settings = useTenantSettings()

  if (usage.isPending) return <Spinner label={USAGE_COPY.loading} />

  if (usage.data === undefined) {
    const failure = [usage.error].find(isApiError)
    if (failure === undefined) return <EmptyState title={USAGE_COPY.title} />
    return <EmptyState title={USAGE_COPY.title} description={failure.message} />
  }

  return (
    <div className="usage">
      <h1>{USAGE_COPY.title}</h1>
      <BudgetPanel usage={usage.data} now={new Date()} />
      <CostEstimate usage={usage.data} settings={settings} />
      <VolumeCard byKind={usage.data.byKind} />
      <ProviderCard byProvider={usage.data.byProvider} />
      <TrendCard />
    </div>
  )
}

export const UsagePage = () => {
  const { membership } = useReadySession()

  if (!canManageSettings(membership.role)) {
    return <EmptyState title={USAGE_COPY.forbidden} description={USAGE_COPY.forbiddenBody} />
  }

  return <UsageReport />
}
